import type { PrototypeState } from '../controllerState';
import type { Root } from '../prototypeTypes';
import { recordRuntimeError } from '../prototypeRuntimeSupport';
import { clearOperationFeedback, paintOperationFeedback } from '../operationFeedback';
import { handleApiError } from '../authGuards';
import { PrototypeApiError } from '../apiClient';
import { showFieldError, clearFieldError } from '../wizardValidation';
import { feedbackErrorMessage } from '../feedbackErrorPresentation';
import { feedbackTicketIdFromBrowserLocation } from '../notificationActionRoute';
import { resetTransientSurfaceState } from '../transientSurfaceState';

interface FeedbackContext {
	generation: number;
	ticketId: string | null;
	token: string;
}

const feedbackContexts = new WeakMap<object, FeedbackContext>();
const replyAttempts = new WeakMap<object, symbol>();

export function invalidateFeedbackContext(root: Root, ticketId: string | null = null): FeedbackContext {
	const previous = feedbackContexts.get(root as object);
	const generation = (previous?.generation ?? 0) + 1;
	const context = { generation, ticketId, token: `feedback:${generation}` };
	feedbackContexts.set(root as object, context);
	const view = root.querySelector<HTMLElement>('#view-feedback');
	if (view) {
		view.dataset.feedbackGeneration = String(context.generation);
		view.dataset.uiAttempt = context.token;
	}
	return context;
}

function feedbackViewIsActive(root: Root): boolean {
	const view = root.querySelector<HTMLElement>('#view-feedback');
	return view === null || view.classList.contains('active');
}

function myFeedbackTabIsActive(root: Root): boolean {
	const tab = root.querySelector<HTMLElement>('#feedbackTabMy');
	return tab === null || tab.style.display !== 'none';
}

function isCurrentFeedbackContext(root: Root, context: FeedbackContext, detail = false): boolean {
	const view = root.querySelector<HTMLElement>('#view-feedback');
	if (feedbackContexts.get(root as object) !== context || !feedbackViewIsActive(root) || !myFeedbackTabIsActive(root) || (view !== null && view.dataset.uiAttempt !== context.token)) return false;
	if (!detail) return true;
	const detailView = root.querySelector<HTMLElement>('#feedbackDetailView');
	return detailView === null || detailView.style.display !== 'none';
}

const FEEDBACK_CATEGORY_LABELS: Record<string, string> = {
	bug: 'Bug Report',
	feature: 'Feature Request',
	content: 'Content Suggestion',
	billing: 'Billing',
	other: 'Other',
};

function categoryLabel(slug: string): string {
	return FEEDBACK_CATEGORY_LABELS[slug] ?? slug;
}

function terminalStatusNotice(status: string): string {
	if (status === 'resolved') return '<strong style="font-size:14px;">Feedback closed</strong><p style="font-size:13px;color:var(--fg-secondary);margin-top:4px;">This feedback has been closed.</p>';
	return '';
}

function clearFeedbackTicketRoute(ticketId: string): void {
	if (typeof window === 'undefined' || !window.history) return;
	if (feedbackTicketIdFromBrowserLocation(window.location) !== ticketId) return;
	window.history.replaceState({ view: 'feedback' }, '', '/feedback');
}

function restoreFeedbackList(root: Root, ticketId: string): void {
	const list = root.querySelector<HTMLElement>('#feedbackListView');
	const detail = root.querySelector<HTMLElement>('#feedbackDetailView');
	const header = root.querySelector<HTMLElement>('#feedbackDetailHeader');
	const conversation = root.querySelector<HTMLElement>('#feedbackConversation');
	const replyForm = root.querySelector<HTMLElement>('#feedbackReplyForm');
	const statusNotice = root.querySelector<HTMLElement>('#feedbackStatusNotice');
	if (list) {
		list.hidden = false;
		list.style.display = '';
	}
	if (detail) {
		detail.hidden = true;
		detail.style.display = 'none';
	}
	if (header) header.innerHTML = '';
	if (conversation) conversation.innerHTML = '';
	if (replyForm) replyForm.style.display = 'none';
	if (statusNotice) { statusNotice.style.display = 'none'; statusNotice.innerHTML = ''; }
	clearFeedbackTicketRoute(ticketId);
}

export function switchToFeedbackTab(root: Root, tab: 'send' | 'my'): void {
	const sendTab = root.querySelector<HTMLElement>('#feedbackTabSend');
	const myTab = root.querySelector<HTMLElement>('#feedbackTabMy');
	if (sendTab) sendTab.style.display = tab === 'send' ? '' : 'none';
	if (myTab) myTab.style.display = tab === 'my' ? '' : 'none';
}

export function activateFeedbackTab(root: Root, tab: 'send' | 'my'): void {
	root.querySelectorAll<HTMLElement>('.feedback-tab-btn').forEach((btn) => {
		const isActive = btn.dataset.tab === tab;
		btn.classList.toggle('active', isActive);
		btn.style.borderBottom = isActive ? '2px solid var(--accent)' : '2px solid transparent';
		btn.style.color = isActive ? 'var(--accent)' : 'var(--fg-tertiary)';
	});
}

export async function loadMyFeedback(root: Root, state: PrototypeState): Promise<boolean> {
	const context = invalidateFeedbackContext(root);
	const listView = root.querySelector<HTMLElement>('#feedbackListView');
	const loading = root.querySelector<HTMLElement>('#feedbackListLoading');
	const loginPrompt = root.querySelector<HTMLElement>('#feedbackLoginPrompt');
	const empty = root.querySelector<HTMLElement>('#feedbackEmpty');
	const list = root.querySelector<HTMLElement>('#feedbackList');
	const detail = root.querySelector<HTMLElement>('#feedbackDetailView');
	if (detail) {
		detail.hidden = true;
		detail.style.display = 'none';
	}
	if (listView) {
		listView.hidden = false;
		listView.style.display = '';
	}
	for (const node of [loading, loginPrompt, empty, list]) if (node) node.style.display = 'none';
	if (!state.isLoggedIn) {
		if (!isCurrentFeedbackContext(root, context)) return false;
		if (loginPrompt) loginPrompt.style.display = '';
		return true;
	}
	if (loading) loading.style.display = '';
	try {
		const res = await state.runtime.api.myFeedback();
		if (!isCurrentFeedbackContext(root, context)) return false;
		if (loading) loading.style.display = 'none';
		if (res.feedbacks.length === 0) {
			if (empty) empty.style.display = '';
			return true;
		}
		if (list) {
			list.innerHTML = res.feedbacks.map((fb) => `
				<div style="background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-lg);padding:16px 20px;margin-bottom:12px;cursor:pointer;transition:all var(--duration-fast);"
					data-proto-click="openFeedbackDetail('${escapeHtml(fb.id)}')">
					<div style="margin-bottom:8px;">
						<span style="font-size:11px;font-weight:600;color:var(--fg-tertiary);">${escapeHtml(categoryLabel(fb.category))}</span>
					</div>
					<h4 style="font-size:15px;font-weight:600;margin-bottom:4px;">${escapeHtml(fb.subject)}</h4>
					<div style="display:flex;align-items:center;gap:12px;font-size:12px;color:var(--fg-tertiary);">
						<span>${formatDate(fb.createdAt)}</span>
						${fb.hasAttachment ? '<span>Attachment included</span>' : ''}
						${fb.replyCount > 0 ? `<span>• ${fb.replyCount} ${fb.replyCount === 1 ? 'reply' : 'replies'}</span>` : ''}
					</div>
				</div>`).join('');
			list.style.display = '';
		}
		return true;
	} catch (error) {
		if (!isCurrentFeedbackContext(root, context)) return false;
		recordRuntimeError(state.runtime, error);
		if (loading) loading.style.display = 'none';
		if (list) {
			list.innerHTML = '<div style="text-align:center;padding:24px;color:var(--fg-tertiary);"><p>Failed to load feedback. Please try again.</p><button class="btn btn-outline" data-proto-click="loadMyFeedback()">Retry</button></div>';
			list.style.display = '';
		}
		return true;
	}
}

export async function openFeedbackDetail(root: Root, state: PrototypeState, ticketId: string): Promise<void> {
	const context = invalidateFeedbackContext(root, ticketId);
	const list = root.querySelector<HTMLElement>('#feedbackListView');
	const detail = root.querySelector<HTMLElement>('#feedbackDetailView');
	const header = root.querySelector<HTMLElement>('#feedbackDetailHeader');
	const conversation = root.querySelector<HTMLElement>('#feedbackConversation');
	const replyForm = root.querySelector<HTMLElement>('#feedbackReplyForm');
	const statusNotice = root.querySelector<HTMLElement>('#feedbackStatusNotice');
	const replyBody = root.querySelector<HTMLTextAreaElement>('#feedbackReplyBody');
	if (replyForm) resetTransientSurfaceState(replyForm);
	if (replyBody) replyBody.value = '';
	if (list) {
		list.hidden = true;
		list.style.display = 'none';
	}
	if (detail) {
		detail.hidden = false;
		detail.style.display = '';
	}
	if (header) header.innerHTML = '<p style="color:var(--fg-tertiary);">Loading...</p>';
	if (conversation) conversation.innerHTML = '';
	if (replyForm) replyForm.style.display = 'none';
	if (statusNotice) { statusNotice.style.display = 'none'; statusNotice.innerHTML = ''; }
	try {
		const res = await state.runtime.api.feedbackDetail(ticketId);
		if (!isCurrentFeedbackContext(root, context, true)) return;
		if (header) header.innerHTML = `
			<div style="margin-bottom:12px;">
				<span style="font-size:11px;font-weight:600;color:var(--fg-tertiary);">${escapeHtml(categoryLabel(res.ticket.category))}</span>
			</div>
			<h3 style="font-family:var(--font-display);font-size:18px;font-weight:600;margin-bottom:8px;">${escapeHtml(res.ticket.subject)}</h3>
			<p style="font-size:13px;color:var(--fg-tertiary);">Submitted ${formatDate(res.ticket.createdAt)}</p>
			<p style="font-size:14px;line-height:1.55;white-space:pre-wrap;margin-top:16px;">${escapeHtml(res.ticket.details ?? '')}</p>
			${res.ticket.hasAttachment ? `<a class="btn btn-outline" href="${escapeHtml(state.runtime.api.feedbackAttachmentDownload(ticketId))}" target="_blank" rel="noopener" style="margin-top:10px;display:inline-flex;">Download attachment</a>` : ''}`;
		if (conversation) {
			conversation.innerHTML = res.messages.length === 0
				? '<div style="text-align:center;font-size:13px;color:var(--fg-tertiary);padding:20px 0;"><svg aria-hidden="true" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="display:block;margin:0 auto 8px;"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg><span>No replies yet. We\'ll get back to you soon.</span></div>'
				: res.messages.map((msg) => renderMessage(msg)).join('');
		}
		if (replyForm) {
			replyForm.style.display = res.ticket.status === 'resolved' ? 'none' : '';
			replyForm.setAttribute('data-ticket-id', ticketId);
			replyForm.setAttribute('data-ticket-version', String(res.ticket.version));
		}
		if (statusNotice) {
			const notice = terminalStatusNotice(res.ticket.status);
			statusNotice.innerHTML = notice;
			statusNotice.style.display = notice ? '' : 'none';
		}
	} catch (error) {
		if (!isCurrentFeedbackContext(root, context, true)) return;
		if (error instanceof PrototypeApiError && error.status === 404) {
			restoreFeedbackList(root, ticketId);
			invalidateFeedbackContext(root);
			return;
		}
		recordRuntimeError(state.runtime, error);
		if (header) header.innerHTML = `<div style="color:var(--fg-tertiary);text-align:center;padding:24px;"><p>Failed to load feedback. Please try again.</p><button class="btn btn-outline" data-proto-click="openFeedbackDetail('${escapeHtml(ticketId)}')">Retry</button></div>`;
		if (conversation) conversation.innerHTML = '';
		if (replyForm) replyForm.style.display = 'none';
	}
}

export async function submitFeedbackReply(root: Root, state: PrototypeState): Promise<void> {
	const replyForm = root.querySelector<HTMLElement>('#feedbackReplyForm');
	const body = root.querySelector<HTMLTextAreaElement>('#feedbackReplyBody');
	const btn = root.querySelector<HTMLButtonElement>('#feedbackReplyBtn');
	const ticketId = replyForm?.getAttribute('data-ticket-id');
	const expectedVersion = Number.parseInt(replyForm?.getAttribute('data-ticket-version') ?? '', 10);
	const replyText = body?.value?.trim() ?? '';
	if (!ticketId) return;
	const context = feedbackContexts.get(root as object) ?? invalidateFeedbackContext(root, ticketId);
	if (context.ticketId !== ticketId || !isCurrentFeedbackContext(root, context, true)) return;
	const attempt = Symbol('feedback-reply');
	replyAttempts.set(root as object, attempt);
	const isCurrent = () => replyAttempts.get(root as object) === attempt && isCurrentFeedbackContext(root, context, true);
	clearOperationFeedback(root, 'feedbackReplyAlert');
	if (replyText.length < 2 || replyText.length > 2000) {
		showFieldError(root, 'feedbackReplyBody', replyText.length === 0 ? 'Please enter your reply with at least 2 characters.' : replyText.length > 2000 ? 'Reply must not exceed 2000 characters.' : 'Reply must be at least 2 characters.');
		paintOperationFeedback(root, 'feedbackReplyAlert', 'Please review the highlighted fields and try again.');
		return;
	}
	clearFieldError(root, 'feedbackReplyBody');
	if (btn) { btn.disabled = true; btn.textContent = 'Sending...'; }
	try {
		if (!Number.isInteger(expectedVersion) || expectedVersion < 0) throw new Error('feedback_version_missing');
		await state.runtime.api.replyFeedback(ticketId, { body: replyText, expectedVersion, mutationId: crypto.randomUUID() });
		if (!isCurrent()) return;
		if (body) body.value = '';
		await openFeedbackDetail(root, state, ticketId);
	} catch (error) {
		if (!isCurrent()) return;
		if (error instanceof PrototypeApiError && (error.status === 401 || error.status === 403)) {
			handleApiError(state.runtime, state.effectiveAccessPlan, state.isLoggedIn, error, { root, intent: 'feedback' });
			return;
		}
		recordRuntimeError(state.runtime, error);
		paintOperationFeedback(root, 'feedbackReplyAlert', feedbackErrorMessage(error, 'Failed to send reply. Please try again.'));
	} finally {
		if (isCurrent() && btn) { btn.disabled = false; btn.textContent = 'Send Reply'; }
	}
}

function renderMessage(msg: { authorRole: string; body: string; createdAt: string }): string {
	const isUser = msg.authorRole === 'user';
	return `<div style="display:flex;justify-content:${isUser ? 'flex-end' : 'flex-start'};">
		<div style="max-width:70%;background:${isUser ? 'var(--accent-light)' : 'var(--surface)'};border:1px solid ${isUser ? 'var(--accent)' : 'var(--border)'};border-radius:var(--radius-lg);padding:12px 16px;">
			<div style="font-size:11px;font-weight:600;color:${isUser ? 'var(--accent)' : 'var(--fg-tertiary)'};margin-bottom:4px;">${isUser ? 'You' : 'DeepMox Team'}</div>
			<div style="font-size:14px;line-height:1.5;white-space:pre-wrap;">${escapeHtml(msg.body)}</div>
			<div style="font-size:11px;color:var(--fg-tertiary);margin-top:6px;">${formatDate(msg.createdAt)}</div>
		</div></div>`;
}

function escapeHtml(value: string): string {
	return value.replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]!);
}

function formatDate(value: string): string {
	return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
